'use client';

import type { ActivityRow } from '@/components/chat/thread-model';
import { ToolIcon, formatToolCall, toolDisplay } from '@/components/chat/tool-display';
import type { FunctionComponent } from 'react';

interface ActivityTimelineProps {
  rows: ActivityRow[];
  showDetails: boolean;
}

export const ActivityTimeline: FunctionComponent<ActivityTimelineProps> = ({ rows, showDetails }) => (
  <div className="rounded-[7px] border border-line bg-bg px-2.5 py-1.5 flex flex-col">
    {rows.map(row => {
      const display = toolDisplay(row.tool_name, row.args);
      return (
        <div key={row.id} className="flex flex-col py-1 animate-[slide-in_0.18s_ease-out]">
          <div className="flex items-center gap-2">
            <span className={row.status === 'running' ? 'text-acc' : 'text-ink-3'}>
              <ToolIcon kind={display.icon} />
            </span>
            <span className="text-[11.5px] text-ink-2 leading-[1.4] flex-1 min-w-0">{display.label}</span>
            <RowStatus status={row.status} />
          </div>
          {showDetails ? (
            <div className="pl-[19px] font-mono text-[10.5px] text-ink-4 break-all">
              {formatToolCall(row.tool_name, row.args)}
            </div>
          ) : null}
        </div>
      );
    })}
  </div>
);

const RowStatus: FunctionComponent<{ status: ActivityRow['status'] }> = ({ status }) => {
  if (status === 'running') {
    return (
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        className="animate-spin text-acc flex-shrink-0"
        aria-label="running"
      >
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    );
  }
  if (status === 'ok') {
    return (
      <span className="text-green text-[11px] leading-none flex-shrink-0" aria-label="done">
        ✓
      </span>
    );
  }
  return (
    <span className="text-rose text-[11px] leading-none flex-shrink-0" aria-label="failed">
      ✗
    </span>
  );
};
